import { useCallback, useEffect, useMemo, useState } from "react";
import styles from "./viewStandaloneLesson.module.scss";
import { useSllGetLessonInfoByUniqueNameQuery } from "@api/graphql-codegen";
import { useLocation, useHistory } from "react-router-dom";
import LessonProgressModal from "@src/components/lesson-progress-modal/LessonProgressModal";
import GradeSelectModal from "@src/components/grade-select-modal/GradeSelectModal";
import Spinner from "@src/components/spinner/Spinner";
import { useAnalytics, Events, useRolesPermissions, useUser } from "@src/hooks";
import { UserRoleEnum } from "@src/types";

const AU_PREPROGRAM_QUIZ_UNIQUE_NAME = "preprogram-quiz-2";

const ViewStandaloneLesson = () => {
  const history = useHistory();
  const { pathname, search } = useLocation();
  const uniqueName = pathname.split("/")[2];
  // A link shared from a grade card carries that grade, so a lesson opened by URL still knows
  // which year was taught, exactly as clicking the card would have told us.
  const launchedGrade = new URLSearchParams(search).get("grade") ?? undefined;
  const { trackEvent } = useAnalytics();
  const [user] = useUser();

  const isAUPreProgramQuiz = uniqueName === AU_PREPROGRAM_QUIZ_UNIQUE_NAME;

  const [showSelectGradeModal, setShowSelectGradeModal] = useState(false);
  const [showAusPreProgram, setShowAusPreProgram] = useState(false);
  const [selectedGrade, setSelectedGrade] = useState("");

  const isSuperAdmin = useRolesPermissions([UserRoleEnum.SuperAdmin]);
  const isAdmin = useRolesPermissions([
    UserRoleEnum.Admin,
    UserRoleEnum.DistrictAdmin,
  ]);

  const openAUPrePromQuizUnit = useCallback((e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setShowSelectGradeModal(true);
  }, []);

  useEffect(() => {
    if (isAUPreProgramQuiz) {
      openAUPrePromQuizUnit();
    }
  }, [isAUPreProgramQuiz, openAUPrePromQuizUnit]);

  const handleOpenAusPreProgramQuiz = useCallback(
    (programId: string) => {
      if (programId) {
        setSelectedGrade(programId);
        setShowAusPreProgram(true);
        setShowSelectGradeModal(false);
      }
      trackEvent(Events.actions.preProgramQuizOpen, {
        unit_name: "Pre-Program Quiz",
        schoolId: user?.schools?.[0]?.id,
        schoolName: user?.schools?.[0]?.name,
        userId: user?.id,
        userName: `${user?.firstName} ${user?.lastName}`,
      });
    },
    [user, trackEvent],
  );

  const { data, loading } = useSllGetLessonInfoByUniqueNameQuery({
    fetchPolicy: "network-only",
    onCompleted(data) {
      if (!data.sllGetLessonInfoByUniqueName) {
        history.push("/dashboard");
      }
    },
    onError() {
      history.push("/dashboard");
    },
    skip: !uniqueName || isAUPreProgramQuiz,
    variables: { input: { uniqueName } },
  });

  const isOpReel = useMemo(() => {
    if (data && data.sllGetLessonInfoByUniqueName) {
      return !!data.sllGetLessonInfoByUniqueName.isOpReel;
    }
    return false;
  }, [data]);

  const handleCloseModal = useCallback(() => {
    if (isOpReel) {
      if (isSuperAdmin) {
        history.push("/super/programs");
      } else if (isAdmin) {
        history.push("/admin/programs");
      } else {
        history.push("/programs");
      }
    } else {
      history.push("lessons?tab=library");
    }
  }, [history, isAdmin, isOpReel, isSuperAdmin]);

  const handleCloseAusPreProgram = useCallback(() => {
    setShowAusPreProgram(false);
    history.push("/lessons?tab=library");
  }, [history]);

  if (isAUPreProgramQuiz) {
    return (
      <div className={styles.page}>
        {showSelectGradeModal && (
          <GradeSelectModal
            open={showSelectGradeModal}
            onClose={() => {
              setShowSelectGradeModal(false);
              history.push("lessons?tab=library");
            }}
            onContinue={handleOpenAusPreProgramQuiz}
          />
        )}
        {showAusPreProgram && (
          <LessonProgressModal
            open={showAusPreProgram}
            onClose={handleCloseAusPreProgram}
            lessonId=""
            isAusPreProgramQuiz={true}
            gradeName={selectedGrade}
          />
        )}
      </div>
    );
  }

  return (
    <div className={styles.page}>
      {loading ? (
        <div className={styles.spinner}>
          <Spinner />
        </div>
      ) : data?.sllGetLessonInfoByUniqueName ? (
        <LessonProgressModal
          open
          onClose={handleCloseModal}
          lessonId={data.sllGetLessonInfoByUniqueName.id}
          launchedGrade={launchedGrade}
        />
      ) : null}
    </div>
  );
};

export default ViewStandaloneLesson;
